import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Field,
  FieldError,
  FieldLabel,
  Input,
} from '@/components/ui';
import { PrivatePackageSessionDatesField } from '@/components/lessons/PrivatePackageSessionDatesField';
import { LessonCoverImageField } from '@/components/lessons/LessonCoverImageField';
import { formatCurrency, type Currency } from '@/utils/currency';
import { parseLocalDateYmd } from '@court-sync/shared';
import type { AgeGroup, Coach, Court, LessonFormData, Location, Program, Region } from '@court-sync/shared';
import {
  Control,
  FieldErrors,
  UseFormRegister,
} from 'react-hook-form';
import { AcademySportSelectField } from '@/components/sports/AcademySportSelectField';
import { Layers, Info, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { sortPrivateSessionDates } from '@/utils/quickLessonForm';
import {
  TooltipContent,
  TooltipProvider,
  TooltipRoot,
  TooltipTrigger,
} from '@/components/ui/Tooltip';

export type QuickLessonFormDetailLevel = 'quick' | 'advanced';

function FormSection({
  title,
  description,
  children,
  className,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('space-y-4', className)}>
      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h3>
        {description ? (
          <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
        ) : null}
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">{children}</div>
    </section>
  );
}

export interface QuickLessonCreateFormProps {
  register: UseFormRegister<LessonFormData>;
  control: Control<LessonFormData>;
  academyId?: string | null;
  errors: FieldErrors<LessonFormData>;
  formDetailLevel: QuickLessonFormDetailLevel;
  onFormDetailLevelChange: (level: QuickLessonFormDetailLevel) => void;
  syncPrivateSessionDatesToForm: (dates: string[]) => void;
  watchedPrivateSessionDates: string[];
  watchedStartDate: string | undefined;
  watchedPrice: number;
  hasMultiLocation: boolean;
  locations: Location[];
  activeCourtsForPicker: Court[];
  selectedCourtIds: string[];
  toggleCourtSelection: (courtId: string) => void;
  coaches: Coach[];
  coachesLoading: boolean;
  selectedCoachIds: string[];
  toggleCoachSelection: (coachId: string) => void;
  academyAgeGroups: AgeGroup[];
  hasProgramsAccess: boolean;
  programs: Program[];
  watchedProgramId: string | null | undefined;
  onApplyProgramTemplate: (programId: string | null) => void;
  programTemplateHelperText: string;
  currencySymbol: string;
  academyCurrency: Currency;
  academyRegion?: Region;
  coverDisplayUrl: string;
  uploadingCover: boolean;
  submitting: boolean;
  onCoverFile: (file: File) => void;
  onCoverRemove: () => void;
}

export function QuickLessonCreateForm({
  register,
  control,
  academyId,
  errors,
  formDetailLevel,
  onFormDetailLevelChange,
  syncPrivateSessionDatesToForm,
  watchedPrivateSessionDates,
  watchedStartDate,
  watchedPrice,
  hasMultiLocation,
  locations,
  activeCourtsForPicker,
  selectedCourtIds,
  toggleCourtSelection,
  coaches,
  coachesLoading,
  selectedCoachIds,
  toggleCoachSelection,
  academyAgeGroups,
  hasProgramsAccess,
  programs,
  watchedProgramId,
  onApplyProgramTemplate,
  programTemplateHelperText,
  currencySymbol,
  academyCurrency,
  academyRegion,
  coverDisplayUrl,
  uploadingCover,
  submitting,
  onCoverFile,
  onCoverRemove,
}: QuickLessonCreateFormProps) {
  const sortedSessionDates = sortPrivateSessionDates(watchedPrivateSessionDates);
  const showAdvanced = formDetailLevel === 'advanced';
  const activeCoaches = coaches.filter((c) => c.isActive);
  const [structuredLessonsHelpOpen, setStructuredLessonsHelpOpen] = useState(false);

  return (
    <div className="space-y-8">
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
        <div
          className="inline-flex rounded-lg border border-border bg-muted/40 p-0.5"
          role="group"
          aria-label="Form detail level"
        >
          {(['quick', 'advanced'] as const).map((level) => (
            <button
              key={level}
              type="button"
              className={cn(
                'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                formDetailLevel === level
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
              onClick={() => onFormDetailLevelChange(level)}
            >
              {level === 'quick' ? 'Quick' : 'All fields'}
            </button>
          ))}
        </div>

        <TooltipProvider delayDuration={0}>
          <TooltipRoot
            open={structuredLessonsHelpOpen}
            onOpenChange={setStructuredLessonsHelpOpen}
          >
            <TooltipTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-orange-800 underline-offset-2 hover:underline dark:text-orange-200"
              >
                <Info className="h-3.5 w-3.5 shrink-0" aria-hidden />
                Looking for more structured lessons?
              </button>
            </TooltipTrigger>
            <TooltipContent
              side="bottom"
              align="end"
              sideOffset={8}
              className="max-w-[min(20rem,90vw)] border-0 bg-gray-900 p-3 text-left text-sm text-white shadow-xl dark:bg-gray-800"
            >
              <p className="text-xs leading-relaxed text-gray-100">
                This form is to create ad-hoc private or publicly bookable lessons — not tied to a
                term or camp. Set capacity above 1 for small-group sessions students can book on your
                public page.
              </p>
              <p className="mt-2 text-xs font-medium text-white">
                If you&apos;re looking for more structure, check out:
              </p>
              <ul className="mt-1.5 space-y-1 text-xs">
                <li>
                  <Link
                    to="/admin/terms"
                    className="font-medium text-white underline underline-offset-2 hover:text-gray-200"
                    onClick={() => setStructuredLessonsHelpOpen(false)}
                  >
                    Terms &amp; group programs
                  </Link>
                </li>
                <li>
                  <Link
                    to="/admin/camps"
                    className="font-medium text-white underline underline-offset-2 hover:text-gray-200"
                    onClick={() => setStructuredLessonsHelpOpen(false)}
                  >
                    Camps
                  </Link>
                </li>
              </ul>
            </TooltipContent>
          </TooltipRoot>
        </TooltipProvider>
        </div>

        <FormSection title="Basics">
          <Field className="md:col-span-2" data-invalid={!!errors.name?.message}>
            <FieldLabel htmlFor="quick-lesson-name">Lesson name *</FieldLabel>
            <Input
              id="quick-lesson-name"
              aria-invalid={!!errors.name?.message}
              {...register('name')}
              placeholder="Private hitting session"
            />
            <FieldError>{errors.name?.message}</FieldError>
          </Field>

          {hasProgramsAccess ? (
            <Field className="md:col-span-2" data-invalid={!!errors.programId?.message}>
              <FieldLabel htmlFor="quick-programId" className="flex items-center gap-2">
                <Layers className="h-4 w-4 text-muted-foreground" aria-hidden />
                Development program
              </FieldLabel>
              <select
                id="quick-programId"
                className="input"
                value={watchedProgramId ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  onApplyProgramTemplate(v ? v : null);
                }}
              >
                <option value="">None</option>
                {[...programs]
                  .sort((a, b) => {
                    if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
                    return a.name.localeCompare(b.name);
                  })
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                      {!p.isActive ? ' (inactive)' : ''}
                    </option>
                  ))}
              </select>
              <FieldError>{errors.programId?.message}</FieldError>
              <p className="mt-1 text-xs text-muted-foreground">{programTemplateHelperText}</p>
            </Field>
          ) : null}

          <AcademySportSelectField
            academyId={academyId}
            control={control}
            name="academySportId"
            required
            className="md:col-span-2"
          />
        </FormSection>
      </div>

      <FormSection
        title="Schedule"
        description="Click days on the calendar to add or remove sessions. Add multiple dates for a fixed package."
      >
        <div className="md:col-span-2">
          <PrivatePackageSessionDatesField
            showDescription={false}
            selectedDates={sortedSessionDates}
            onChange={syncPrivateSessionDatesToForm}
            initialFocusDate={
              sortedSessionDates[0]
                ? parseLocalDateYmd(sortedSessionDates[0]) ?? undefined
                : watchedStartDate
                  ? parseLocalDateYmd(watchedStartDate) ?? undefined
                  : undefined
            }
            region={academyRegion}
          />
          {errors.termIds && sortedSessionDates.length === 0 ? (
            <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.termIds.message}</p>
          ) : null}
        </div>

        <Field data-invalid={!!errors.startTime?.message}>
          <FieldLabel htmlFor="quick-startTime">Start time *</FieldLabel>
          <Input
            id="quick-startTime"
            type="time"
            aria-invalid={!!errors.startTime?.message}
            {...register('startTime')}
          />
          <FieldError>{errors.startTime?.message}</FieldError>
        </Field>

        <Field data-invalid={!!errors.endTime?.message}>
          <FieldLabel htmlFor="quick-endTime">End time *</FieldLabel>
          <Input
            id="quick-endTime"
            type="time"
            aria-invalid={!!errors.endTime?.message}
            {...register('endTime')}
          />
          <FieldError>{errors.endTime?.message}</FieldError>
        </Field>
      </FormSection>

      <FormSection title="Staff & courts">
        {hasMultiLocation && locations.length > 0 ? (
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Location (optional)
            </label>
            <select {...register('locationId')} className="input">
              <option value="">All locations — show all courts</option>
              {locations
                .filter((l) => l.isActive)
                .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
                .map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name}
                  </option>
                ))}
            </select>
          </div>
        ) : null}

        <div className={hasMultiLocation && locations.length > 0 ? '' : 'md:col-span-2'}>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Courts * ({selectedCourtIds.length} selected)
          </label>
          <div className="max-h-32 overflow-y-auto rounded-md border border-gray-300 bg-white p-2 dark:border-gray-600 dark:bg-gray-800">
            {activeCourtsForPicker.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">No active courts available</p>
            ) : (
              activeCourtsForPicker.map((court) => (
                <label
                  key={court.id}
                  className="flex items-center space-x-2 rounded px-2 py-1 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <input
                    type="checkbox"
                    checked={selectedCourtIds.includes(court.id)}
                    onChange={() => toggleCourtSelection(court.id)}
                    className="rounded border-gray-300 text-blue-600 shadow-sm"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{court.name}</span>
                </label>
              ))
            )}
          </div>
          {errors.courtIds ? (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.courtIds.message}</p>
          ) : null}
        </div>

        <div className="md:col-span-2">
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Coaches * ({selectedCoachIds.length} selected)
          </label>
          <div className="max-h-32 overflow-y-auto rounded-md border border-gray-300 bg-white p-2 dark:border-gray-600 dark:bg-gray-800">
            {coachesLoading ? (
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin shrink-0" aria-hidden />
                Loading coaches…
              </p>
            ) : activeCoaches.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">No active coaches available</p>
            ) : (
              activeCoaches.map((coach) => (
                <label
                  key={coach.id}
                  className="flex items-center space-x-2 rounded px-2 py-1 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <input
                    type="checkbox"
                    checked={selectedCoachIds.includes(coach.id)}
                    onChange={() => toggleCoachSelection(coach.id)}
                    className="rounded border-gray-300 text-blue-600 shadow-sm"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {coach.firstName} {coach.lastName}
                  </span>
                </label>
              ))
            )}
          </div>
          {errors.coachIds ? (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.coachIds.message}</p>
          ) : null}
        </div>
      </FormSection>

      <FormSection title="Pricing & capacity">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Price per session ({currencySymbol}) *
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">
              {currencySymbol}
            </span>
            <input
              type="number"
              step="0.01"
              onWheel={(e) => e.currentTarget.blur()}
              {...register('price', { valueAsNumber: true })}
              className="input pl-12"
              placeholder="50.00"
            />
          </div>
          {errors.price ? (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.price.message}</p>
          ) : null}
          {watchedPrice > 0 && sortedSessionDates.length > 0 ? (
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
              Total package:{' '}
              {formatCurrency(watchedPrice * sortedSessionDates.length, academyCurrency)} (
              {sortedSessionDates.length} sessions)
            </p>
          ) : null}
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Max students *
          </label>
          <input
            type="number"
            min={1}
            max={50}
            {...register('maxCapacity', { valueAsNumber: true })}
            className={`input w-full ${errors.maxCapacity ? 'border-red-500' : ''}`}
          />
          {errors.maxCapacity ? (
            <p className="mt-1 text-sm text-red-600">{errors.maxCapacity.message}</p>
          ) : null}
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            One booking per student. Use 1 for a true private lesson, or higher for a small group.
          </p>
        </div>

        <div className="md:col-span-2">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              {...register('isActive')}
              className="rounded border-gray-300 text-blue-600 shadow-sm"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Publicly bookable (active on your academy page)
            </span>
          </label>
        </div>
      </FormSection>

      {showAdvanced ? (
        <FormSection
          title="Details & visibility"
          description="Optional fields for public listing, skill tracking, and branding."
        >
          {academyAgeGroups.length > 0 ? (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Age group
              </label>
              <select {...register('ageGroupId')} className="input">
                <option value="">— None —</option>
                {academyAgeGroups.map((ag) => (
                  <option key={ag.id} value={ag.id}>
                    {ag.label}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Age group
                </label>
                <select {...register('ageGroup')} className="input">
                  <option value="all">All ages</option>
                  <option value="juniors">Juniors</option>
                  <option value="adults">Adults</option>
                </select>
              </div>
            </>
          )}

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Skill level
            </label>
            <select {...register('skillLevel')} className="input">
              <option value="any">Any level</option>
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Description
            </label>
            <textarea
              {...register('description')}
              rows={3}
              className="input"
              placeholder="What students can expect from this lesson…"
            />
          </div>

          <div className="md:col-span-2">
            <LessonCoverImageField
              displayUrl={coverDisplayUrl}
              uploading={uploadingCover}
              disabled={submitting || uploadingCover}
              inputId="quick-lesson-cover-upload"
              onFileSelect={onCoverFile}
              onRemove={onCoverRemove}
            />
            <input type="hidden" {...register('imageUrl')} />
          </div>

          <div className="md:col-span-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                {...register('isFeatured')}
                className="rounded border-gray-300 text-blue-600 shadow-sm"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Featured on public academy page
              </span>
            </label>
          </div>
        </FormSection>
      ) : null}

    </div>
  );
}
